import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * 보냉 포장비를 장바구니의 독립된 라인으로 유지하는 요소.
 *
 * `snippets/cold-pack-fee.liquid`이 "포장비가 필요한가 / 지금 몇 개 담겨 있나"를
 * data 속성으로 내보낸다. 이 요소는 그 둘이 어긋날 때만 Cart AJAX API를 호출하고,
 * 호출 뒤 카트 섹션이 다시 렌더되면 속성이 갱신되어 더는 어긋나지 않으므로 멈춘다.
 */

/** 드로어와 카트 페이지가 동시에 떠 있을 때 중복 호출을 막는 공유 잠금. */
let inFlight = null;

/** 한 번 실패하면 같은 페이지 로드에서는 재시도하지 않는다(반복 실패 방지). */
let disabled = false;

class ColdPackFee extends HTMLElement {
  static observedAttributes = ['data-needed', 'data-fee-quantity'];

  connectedCallback() {
    this.#reconcile();
  }

  attributeChangedCallback() {
    this.#reconcile();
  }

  #reconcile() {
    if (disabled || inFlight || !this.isConnected) return;

    const desired = this.dataset.needed === 'true' ? 1 : 0;
    const current = Number(this.dataset.feeQuantity) || 0;

    if (desired === current) return;

    inFlight = this.#setFeeQuantity(desired)
      .catch((error) => {
        disabled = true;
        console.warn('[cold-pack-fee]', error);
      })
      .finally(() => {
        inFlight = null;
      });
  }

  /**
   * 포장비 라인의 수량을 지정한 값으로 맞춘다. 0이면 라인이 사라지고, 라인이 없으면 추가된다.
   * @param {number} quantity
   */
  async #setFeeQuantity(quantity) {
    const sectionIds = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }

    const body = JSON.stringify({
      updates: { [String(this.dataset.variantId)]: quantity },
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const cart = await response.json();

    if (!response.ok || cart.status) {
      throw new Error(cart.description || cart.message || '보냉 포장비 라인을 갱신하지 못했습니다.');
    }

    // 카트 드로어·카트 페이지·헤더 뱃지를 테마의 표준 경로로 갱신한다.
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity > 0 ? 'add' : 'remove',
        context: 'cart',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${this.dataset.variantId}`, quantity }],
        promise: deferred.promise,
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'cold-pack-fee',
        didError: false,
      },
    });
  }
}

if (!customElements.get('cold-pack-fee')) {
  customElements.define('cold-pack-fee', ColdPackFee);
}
